<template>
  <div
    class="view-mode-control"
    role="group"
    :aria-label="label"
    :style="{ '--view-mode-count': visibleOptions.length, '--view-mode-index': selectedIndex }"
  >
    <span class="view-mode-thumb" aria-hidden="true"></span>
    <button
      v-for="option in visibleOptions"
      :key="option.value"
      type="button"
      class="view-mode-option"
      :class="{ selected: modelValue === option.value }"
      :aria-label="option.label"
      :title="option.label"
      :aria-pressed="(modelValue === option.value).toString()"
      @click="$emit('update:modelValue', option.value)"
    >
      <svg v-if="option.value === 'grid'" viewBox="0 0 18 18" aria-hidden="true">
        <rect x="3" y="3" width="4.7" height="4.7" rx=".7" />
        <rect x="10.3" y="3" width="4.7" height="4.7" rx=".7" />
        <rect x="3" y="10.3" width="4.7" height="4.7" rx=".7" />
        <rect x="10.3" y="10.3" width="4.7" height="4.7" rx=".7" />
      </svg>
      <svg v-else viewBox="0 0 18 18" aria-hidden="true">
        <path d="M3 4.25h2M7.2 4.25H15M3 9h2M7.2 9H15M3 13.75h2M7.2 13.75H15" />
      </svg>
      <span class="view-mode-label">{{ option.shortLabel }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ViewModeControl',
  props: {
    modelValue: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: 'Choose a view'
    },
    allowGrid: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      options: [
        { value: 'index', label: 'Index view', shortLabel: 'Index' },
        { value: 'grid', label: 'Grid view', shortLabel: 'Grid' }
      ]
    };
  },
  computed: {
    visibleOptions() {
      return this.allowGrid ? this.options : this.options.filter((option) => option.value !== 'grid');
    },
    selectedIndex() {
      const index = this.visibleOptions.findIndex((option) => option.value === this.modelValue);
      return Math.max(0, index);
    }
  }
};
</script>

<style scoped>
.view-mode-control {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--view-mode-count), 2rem);
  padding: 0.12rem;
  border: 0;
  border-radius: 0.38rem;
  background: rgba(118, 118, 128, 0.055);
  color: #828286;
  isolation: isolate;
}

.view-mode-thumb {
  position: absolute;
  z-index: -1;
  top: 0.12rem;
  left: 0.12rem;
  width: 2rem;
  height: 2rem;
  border-radius: 0.28rem;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06), 0 0 0 0.5px rgba(0, 0, 0, 0.035);
  transform: translateX(calc(var(--view-mode-index) * 2rem));
  pointer-events: none;
  transition: transform 210ms cubic-bezier(0.23, 1, 0.32, 1), background-color 180ms ease;
}

.view-mode-option {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 0.32rem;
  background: transparent;
  color: inherit;
  transition: color 140ms ease, background-color 140ms ease, transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
}

.view-mode-option,
.view-mode-option * {
  cursor: inherit;
}

.view-mode-option.selected { color: #202022; }
.view-mode-option:active { transform: scale(0.92); }
.view-mode-option:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 1px;
}

.view-mode-option svg {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.25;
}

.view-mode-label { display: none; }

@media (hover: hover) and (pointer: fine) {
  .view-mode-option:not(.selected):hover {
    background: rgba(118, 118, 128, 0.09);
    color: #3f3f42;
  }
}

:global(body.dark-mode .view-mode-control) {
  background: rgba(255, 255, 255, 0.045);
  color: #96969b;
}

:global(body.dark-mode .view-mode-thumb) {
  background: rgba(255, 255, 255, 0.105);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18), 0 0 0 0.5px rgba(255, 255, 255, 0.04);
}

:global(body.dark-mode .view-mode-option.selected) { color: #f2f2f4; }

@media (hover: hover) and (pointer: fine) {
  :global(body.dark-mode .view-mode-option:not(.selected):hover) {
    background: rgba(255, 255, 255, 0.065);
    color: #dedee1;
  }
}

@media (max-width: 720px) {
  .view-mode-control {
    width: 100%;
    grid-template-columns: repeat(var(--view-mode-count), minmax(0, 1fr));
  }

  .view-mode-thumb,
  .view-mode-option {
    width: 100%;
    height: 2.2rem;
  }

  .view-mode-thumb {
    width: calc((100% - 0.24rem) / var(--view-mode-count));
    transform: translateX(calc(var(--view-mode-index) * 100%));
  }

  .view-mode-option {
    display: flex;
    gap: 0.38rem;
    justify-content: center;
  }

  .view-mode-label {
    display: inline;
    font-size: 0.7rem;
    letter-spacing: 0.01em;
  }
}

@media (prefers-reduced-motion: reduce) {
  .view-mode-thumb,
  .view-mode-option {
    transition-duration: 80ms;
  }
}
</style>
